// Dashboard view fetch — calls apiv2 clinical_view.getdashboard directly via
// the session-authed ws() transport (the view is ACL_Session, so it can't go
// through WF50). Returns the documented non-throwing {status:"error", reason}
// envelope on failure instead of throwing, so the 3 callers (AccessDashboard,
// EngageWorkspace, useDashboardTable) render an error banner without try/catch.

import { getProgramId } from '@/config/program';
import { getDashboard } from '../endpoints';
import {
  adaptDashboardEnvelope,
  type LegacyDashboardShape,
  type DashboardEnvelope,
} from '@/logic/dashboard';

export interface DashboardViewOpts {
  sort?: string | { field?: string; direction?: string };
  pagination?: { offset?: number; limit?: number };
  search?: string;
  engagement?: string | string[];
  task_roles?: string[];
  selectedFilters?: Record<string, string[]>;
  // The board's opt-ins (BOARD_INCLUDE): the CMS outcome and the roster details. Never sent by Engage.
  include?: string;
  // Defaults to the program in the URL; Engage pins its own, since `/engage` carries none.
  programid?: string;
}

// The fields the dashboard view reads, in the app's own naming.
function buildParamRecord(role: string, options: DashboardViewOpts): Record<string, unknown> {
  const sortField = typeof options.sort === 'string' ? options.sort : options.sort?.field;
  const sortDirection = typeof options.sort === 'string' ? undefined : options.sort?.direction;

  const engagementRaw = options.engagement;
  let engagementCsv: string | undefined;
  if (Array.isArray(engagementRaw)) {
    engagementCsv = engagementRaw.filter(Boolean).join(',');
    if (!engagementCsv) engagementCsv = undefined;
  } else if (typeof engagementRaw === 'string' && engagementRaw.length > 0) {
    engagementCsv = engagementRaw;
  }

  const params: Record<string, unknown> = {
    role,
    programid: options.programid ?? getProgramId(),
    sort: sortField || undefined,
    order: sortDirection || undefined,
    offset: options.pagination?.offset ?? undefined,
    limit: options.pagination?.limit || 50,
    search: options.search || undefined,
    engagement: engagementCsv,
    task_roles: Array.isArray(options.task_roles) && options.task_roles.length > 0
      ? options.task_roles
      : undefined,
    include: options.include || undefined,
  };
  // Filter selections — each `selectedFilters[key]` becomes its own param
  // (alert / lifecycle / conditions).
  const selectedFilters = options.selectedFilters || {};
  Object.entries(selectedFilters).forEach(([key, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      params[key] = values;
    }
  });
  return params;
}

// apiv2 prefixes most dashboard params with `clinical_`, but NOT conditions/sort/
// limit/offset/programid (per Clinical_View_Get_Dashboard.php). The is_list filters
// (engagement/task_roles/alert/lifecycle) go as CSV; `conditions` is CheckerV2
// type:json, so it MUST be a JSON array string (CSV would json_decode-throw → 400).
// A key missing here goes out unprefixed and apiv2 DROPS it without an error, so the
// board renders a full roster that looks filtered — how open_task shipped inert on beta.
const CLINICAL_PREFIXED = new Set(['role', 'search', 'order', 'engagement', 'task_roles', 'alert', 'lifecycle', 'open_task', 'include', 'oap_outcome', 'subtrack', 'report_ready']);
const JSON_PARAMS = new Set(['conditions']);

function toClinicalViewParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    let value: unknown;
    if (JSON_PARAMS.has(k)) {
      const arr = (Array.isArray(v) ? v : [v]).filter(Boolean);
      if (arr.length === 0) return;
      value = JSON.stringify(arr);
    } else {
      value = Array.isArray(v) ? v.filter(Boolean).join(',') : v;
    }
    if (value === '') return;
    if (k === 'order') value = String(value).toLowerCase(); // apiv2 expects asc/desc
    out[CLINICAL_PREFIXED.has(k) ? `clinical_${k}` : k] = value;
  });
  return out;
}

export function toDashboardParams(role: string, options: DashboardViewOpts = {}): Record<string, unknown> {
  return toClinicalViewParams(buildParamRecord(role, options));
}

export async function fetchDashboardView(
  role: string,
  options: DashboardViewOpts = {},
): Promise<LegacyDashboardShape> {
  const apiv2Params = toDashboardParams(role, options);

  try {
    const body = await getDashboard(apiv2Params);
    // apiv2 returns meta.total_count; the adapter reads meta.count — normalize.
    const env: DashboardEnvelope = {
      status: 'success',
      data: body?.data,
      meta: {
        count: body?.meta?.total_count,
        offset: body?.meta?.offset,
        limit: body?.meta?.limit,
        // The opt-ins apiv2 honoured: the roster columns and controls are drawn from it.
        include: body?.meta?.include,
      },
    };
    return adaptDashboardEnvelope(env, role);
  } catch (e) {
    return { status: 'error', reason: (e as Error)?.message || 'network' };
  }
}
